import { createSelector } from '@reduxjs/toolkit';
import { get, isEmpty, map, pipe, filter, sortBy, identity, sortedUniq } from 'lodash/fp';
import { toStringFqn } from 'app/lib/utils';
import { selectProjectEnvRole } from 'app/store/projects/selectors.js';
import { selectSnowflakeConnection } from 'app/store/healthcheck/selectors';
import { SetSnowflakeWarehouseStatus } from './constants';

const selectSlice = get('snowflake');

const selectFqnProp = (_, props) => props.fqn;
const selectDatabaseProp = (_, props) => props.database;
const selectSchemaProp = (_, props) => props.schemaFqn;

const selectLocationsSchemas = createSelector(selectSlice, get('locations.schemas'));
const selectLocationsDatabases = createSelector(selectSlice, get('locations.databases'));
export const selectLocationsError = createSelector(selectSlice, get('locations.error'));
export const selectDestinationLocationsError = createSelector(
    selectSlice,
    get('destinationTreeLocations.error')
);
export const selectIsFilteringLocations = createSelector(selectSlice, get('locations.isFiltering'));
export const selectHaveLocationsFilteredResults = createSelector(
    selectSlice,
    get('locations.hasFilteredResults')
);

export const selectIsFilteringDestinations = createSelector(
    selectSlice,
    get('destinationTreeLocations.isFiltering')
);
const selectDestinationLocations = createSelector(selectSlice, get('destinationTreeLocations'));
export const selectDestinationDatabases = createSelector(
    selectDestinationLocations,
    (destinations) => Object.keys(destinations.databases.byName ?? {})
);

const selectSnowflakeDatabases = createSelector(selectLocationsDatabases, get('byName'));
export const selectSnowflakeSchemas = createSelector(selectLocationsSchemas, get('bySchemaFqn'));

export const selectSnowflakeDatabasesNames = createSelector(selectSnowflakeDatabases, (databases) =>
    Object.keys(databases ?? {})
);

export const selectIsSyncedWithSnowflake = createSelector(
    [selectSnowflakeDatabases],
    (databases) => !isEmpty(databases)
);

export const selectIsLoadingSnowflakeDatabases = createSelector(
    selectLocationsDatabases,
    (databases) => databases.isLoading
);

export const selectIsLoadingSchemasForPropDatabase = createSelector(
    selectSnowflakeDatabases,
    selectDatabaseProp,
    (databases, databaseProp) => databases[databaseProp]?.isLoading
);

export const selectIsLoadingDestinationDatabases = createSelector(
    selectDestinationLocations,
    (destinations) => destinations.databases.isLoading
);

export const selectHaveDestinationsFilteredResults = createSelector(
    selectDestinationLocations,
    (destinations) => destinations.hasFilteredResults
);

export const selectIsLoadingDestinationSchemasForPropDatabase = createSelector(
    selectDestinationLocations,
    selectDatabaseProp,
    (destination, databaseProp) => destination.databases.byName[databaseProp].isLoading
);

export const selectDestinationSchemasForPropDatabase = createSelector(
    selectDatabaseProp,
    selectDestinationLocations,
    (database, destinations) => {
        const schemas = destinations.schemas;
        return pipe(filter({ database }), map('schema'), sortBy(identity), sortedUniq)(schemas);
    }
);

export const selectIsLoadingObjectsForPropSchema = createSelector(
    selectSnowflakeSchemas,
    selectSchemaProp,
    (schemas, schemaProp) => schemas[schemaProp].isLoading
);

export const selectIsValidSnowflakeObject = createSelector(
    selectFqnProp,
    selectSnowflakeSchemas,
    (fqn, schemas) => {
        const schemaFqnString = toStringFqn({ database: fqn.database, schema: fqn.schema });
        const objects = schemas[schemaFqnString]?.sources;
        return objects?.hasOwnProperty(toStringFqn(fqn));
    }
);
export const selectTypeForPropFqn = createSelector(
    selectFqnProp,
    selectSnowflakeSchemas,
    (fqn, schemas) => {
        const schemaFqnString = toStringFqn({ database: fqn.database, schema: fqn.schema });
        const objects = schemas[schemaFqnString].sources;
        return objects[toStringFqn(fqn)]?.sourceType;
    }
);

const selectColumnsByFqn = createSelector(selectSlice, get('columnsByFqn'));
export const selectColumnsBySourceFqn = createSelector(
    selectFqnProp,
    selectColumnsByFqn,
    (fqn, columnsByFqn) => columnsByFqn[fqn]?.columns
);

export const selectColumnsLoadingBySourceFqn = createSelector(
    selectFqnProp,
    selectColumnsByFqn,
    (fqn, columnsByFqn) => columnsByFqn[fqn]?.isLoading
);

const selectWarehouseSlice = createSelector(selectSlice, get('warehouses'));
const selectSnowflakeWarehouses = createSelector(selectWarehouseSlice, get('list.warehouses'));

export const selectSnowflakeWarehouseNames = createSelector(
    selectSnowflakeWarehouses,
    (warehouses) => (warehouses === null ? null : Object.keys(warehouses))
);
export const selectDefaultSnowflakeWarehouse = createSelector(
    selectWarehouseSlice,
    get('list.default')
);
export const selectWarehouseError = createSelector(selectWarehouseSlice, get('error'));

const selectSelectedWarehouse = createSelector(selectWarehouseSlice, get('selectedWarehouse'));
export const selectSelectedWarehouseName = createSelector(selectSelectedWarehouse, get('name'));
export const selectXSmallWarehouses = createSelector(selectSnowflakeWarehouses, (warehouses) => {
    if (warehouses === null) {
        return [];
    }
    return Object.keys(warehouses).filter((warehouse) => warehouses[warehouse]?.isXSmall);
});

const selectIsGetWarehouseLoading = createSelector(selectWarehouseSlice, get('get.isLoading'));
const selectSetWarehouseState = createSelector(selectWarehouseSlice, get('set.state'));
const selectIsSetWarehouseLoading = createSelector(
    [selectSetWarehouseState],
    (state) => state === SetSnowflakeWarehouseStatus.LOADING
);

const selectIsWarehouseListLoading = createSelector(selectWarehouseSlice, get('list.isLoading'));
export const selectIsWarehouseLoading = createSelector(
    [selectIsSetWarehouseLoading, selectIsGetWarehouseLoading, selectIsWarehouseListLoading],
    (isSetWarehouseLoading, isGetWarehouseLoading, isWarehouseListLoading) =>
        isSetWarehouseLoading || isGetWarehouseLoading || isWarehouseListLoading
);

const selectRolesSlice = createSelector(selectSlice, get('roles'));
export const selectSnowflakeRoles = createSelector(selectRolesSlice, get('list'));
export const selectSnowflakeRolesNames = createSelector(selectSnowflakeRoles, map('name'));
export const selectIsSnowflakeRolesLoading = createSelector(selectRolesSlice, get('isLoading'));
export const selectSnowflakeRolesItemInProgress = createSelector(
    selectRolesSlice,
    get('itemInProgress')
);
export const selectSnowflakeRolesError = createSelector(selectRolesSlice, get('error'));

export const selectCanSwitchToProjectEnvironment = createSelector(
    selectSnowflakeRolesNames,
    selectProjectEnvRole,
    (roles, projectRole) => {
        if (isEmpty(roles)) {
            return false;
        }

        return roles.includes(projectRole?.toUpperCase());
    }
);
export const selectIsProjectAndSessionEnvSame = createSelector(
    selectProjectEnvRole,
    selectSnowflakeConnection,
    (projectRole, snowflakeConnection) => {
        const sessionRole = snowflakeConnection?.role;
        return projectRole ? projectRole === sessionRole : false;
    }
);

export const selectShouldShowSwitchToProjectEnvError = createSelector(
    selectCanSwitchToProjectEnvironment,
    selectSnowflakeRolesNames,
    selectIsProjectAndSessionEnvSame,
    (canSwitch, roles, isSameEnv) => !isSameEnv && !canSwitch && !isEmpty(roles)
);

const selectUserSessionWarehousesSlice = createSelector(selectSlice, get('userSessionWarehouses'));
export const selectUserSessionWarehousesList = createSelector(
    selectUserSessionWarehousesSlice,
    get('list')
);

export const selectIsUserSessionWarehousesLoading = createSelector(
    selectUserSessionWarehousesSlice,
    get('isLoading')
);

export const selectUserSessionWarehousesItemInProgress = createSelector(
    selectUserSessionWarehousesSlice,
    get('itemInProgress')
);

export const selectIsDeletingSnowflakeTable = createSelector(selectSlice, get('isDeletingTable'));
